<template>
  <div class="form-cont">
    <div class="form-headline">
      Möchtest du ein TV-Paket dazubuchen?
    </div>
    <div class="form-fields">
      <AppRadioCardButtons
          class="radio-packages mb-0"
          :options="packageOptions"
          v-model="form.television.package"
          toggle
          :grid-template="{
            xs: '1fr',
            sm: '1fr 1fr',
            lg: '1fr',
            xxl: '1fr 1fr',
          }"
      >
        <template v-slot:option="{ option, selected, disabled }">
          <TelevisionPackageCard
              :class="{ selected, disabled }"
              :package="getPackageById(option.value)"
          />
        </template>
      </AppRadioCardButtons>
      <div
          v-if="form.television.package === 'tv-2'"
          class="options-and-addons"
      >
        <div>
          Zusatzoptionen
        </div>
        <div>
          Erweitere dein Standardpaket um weitere Optionen für mehr Entertainment.
        </div>
        <AppRadioCardButtons
            class="radio-options"
            :options="packageOptionOptions"
            v-model="form.television.option"
            toggle
            :grid-template="{
              xs: '1fr',
              sm: '1fr 1fr',
              lg: '1fr',
              xxl: '1fr 1fr',
            }"
        >
          <template v-slot:option="{ option, selected, disabled }">
            <TelevisionOptionCard
                small
                :class="{ selected, disabled }"
                :option="getPackageOptionById(option.value)"
            />
          </template>
        </AppRadioCardButtons>
        <AppRadioCardButtons
            class="radio-addons mb-0"
            :options="packageAddonsOptions"
            v-model="form.television.addons"
            multiple
            :grid-template="{
              xs: '1fr',
              sm: '1fr 1fr',
              xxl: '1fr 1fr 1fr 1fr',
            }"
        >
          <template v-slot:option="{ option, selected, disabled }">
            <TelevisionAddonCard
                small
                :class="{ selected, disabled }"
                :addon="getPackageAddonById(option.value)"
            />
          </template>
        </AppRadioCardButtons>
      </div>
    </div>
    <div
        :class="{
          'bg-gray': form.television.package === 'tv-2',
        }"
    >
      <AppOrderActions
          :labels="{
            previous: 'Zurück',
            next: form.television.package ? 'In den Warenkorb' : 'Ohne TV-Paket weiter',
          }"
          @previous="emit('previous')"
          @next="emit('next')"
          :color-next="product.branch"
          hide-required-hint
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Product from '@/classes/Product';
import AppRadioCardButtons from '@/components/inputs/AppRadioCardButtons.vue';
import TelevisionAddonCard from '@/components/product/TelevisionAddonCard.vue';
import TelevisionOptionCard from '@/components/product/TelevisionOptionCard.vue';
import TelevisionPackageCard from '@/components/product/TelevisionPackageCard.vue';
import AppOrderActions from '@/components/order/order/AppOrderActions.vue';
import TelevisionPackageInterface from '@/interfaces/TelevisionPackageInterface';
import { watch } from 'vue';
import TelevisionProducts from '../../../../public/TelevisionProducts.json';

const props = defineProps<{
  product: Product
  priceParams: any
}>();

const form = defineModel<Record<string, any>>();
const emit = defineEmits(['previous', 'next']);

const customerType = props.priceParams?.customerType || 'private';

const products = TelevisionProducts.products as TelevisionPackageInterface[];
const options = TelevisionProducts.options as unknown as TelevisionPackageInterface[];
const addons = TelevisionProducts.addons as unknown as TelevisionPackageInterface[];

const packageOptions = products.map((p) => ({ label: p.name, value: p.id, customerType: p.customerType })).filter((option) => !Array.isArray(option.customerType) || option.customerType.includes(customerType));

const packageOptionOptions = options.map((p) => ({ label: p.name, value: p.id, customerType: p.customerType })).filter((option) => !Array.isArray(option.customerType) || option.customerType.includes(customerType));
const packageAddonsOptions = addons.map((p) => ({
  label: p.name, value: p.id, excludes: p.price.excludes || [], customerType: p.customerType,
})).filter((option) => !Array.isArray(option.customerType) || option.customerType.includes(customerType));
const getPackageById = (packageId: string): TelevisionPackageInterface => {
  const foundPackage = products.find((p) => p.id === packageId) as TelevisionPackageInterface;

  return foundPackage;
};

const getPackageOptionById = (optionId: string): TelevisionPackageInterface => {
  const foundPackageOption = options.find((o) => o.id === optionId) as TelevisionPackageInterface;

  return foundPackageOption;
};

const getPackageAddonById = (addonId: string): TelevisionPackageInterface => {
  const foundPackageAddon = addons.find((a) => a.id === addonId) as TelevisionPackageInterface;

  return foundPackageAddon;
};

watch(() => form.value.television.package, () => {
  setTimeout(() => {
    window.runIoEqualize();
  }, 50);
}, {
  immediate: true,
});
</script>

<style scoped lang="scss">
@import 'src/scss/component-helpers';

.form-cont {
  height: 100%;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr auto;

  :deep(.radio-packages) {
    padding: var(--modal-card-padding-x);
  }

  > div {
    padding: var(--modal-card-padding-x);

    &.form-fields {
      padding: 0;

      > .app-grid__row > .app-grid__col {
        padding: var(--modal-card-padding-x);

        + .app-grid__col {
          border-top: 1px solid #ECF2F6;
        }
      }
    }

    &:last-child {
      &.bg-gray {
        background-color: #E7F1F5;
      }

      padding-top: 10px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        padding-top: 20px;
      }
    }
  }

  .form-headline {
    font-size: 1rem;
    font-weight: 700;
    padding-bottom: 0;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      font-size: 1.25rem;
    }
  }

  .options-and-addons {
    background-color: #E7F1F5;
    padding: var(--modal-card-padding-x);

    :deep(.radio-options) {
      margin-bottom: 10px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        margin-bottom: 20px;
      }
    }

    > div {
      &:nth-child(1) {
        font-weight: 700;
        font-size: .75rem;
        margin-bottom: 10px;

        @media(min-width: map-get($grid-breakpoints, 'lg')) {
          font-size: 1.125rem;
        }
      }

      &:nth-child(2) {
        margin-bottom: 10px;

        @media(min-width: map-get($grid-breakpoints, 'lg')) {
          margin-bottom: 20px;
        }
      }
    }
  }
}
</style>
